import { useState } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import {
  selectActiveSession,
  selectSelectedSchoolId,
} from "app/modules/schools/selectors";
import { useMutation } from "react-query";
import { uploadS3File } from "app/services/schoolService/misc.service";
import {
  generatePresignedUrlBySchool,
  generatePresignedUrlByIndividual,
} from "app/services/schoolService/aws.service";

const useFolderWiseFileUploader = (useIndividualUpload = false) => {
  const schoolDocId = useSelector(selectSelectedSchoolId);
  const selectedSession = useSelector(selectActiveSession);

  const [state, setState] = useState({
    profilePic: "",
    loading: false,
    errorMessage: "",
  });

  const formattedName = (fileName) => {
    return fileName
      .replace(/[^\w\s.-]/g, "")
      .replace(/\s+/g, "_")
      .toLowerCase();
  };

  const { mutate: uploadS3FileMutate } = useMutation(
    "uploadS3File",
    uploadS3File,
    {
      onSuccess: (success) => {},
      onError: (err) => {
        toast.error(
          err?.response?.data?.message ?? "Failed to upload file to S3"
        );
      },
    }
  );

  const { mutateAsync: generatePresignedUrlBySchoolMutate } = useMutation(
    generatePresignedUrlBySchool,
    {
      onError: (err) => {
        toast.error(err?.response?.data?.message ?? "Failed to generate URL");
        setState((prev) => ({
          ...prev,
          loading: false,
          errorMessage: "Failed to generate URL",
        }));
      },
    }
  );

  const { mutateAsync: generatePresignedUrlByIndividualMutate } = useMutation(
    generatePresignedUrlByIndividual,
    {
      onError: (err) => {
        toast.error(err?.response?.data?.message ?? "Failed to generate URL");
        setState((prev) => ({
          ...prev,
          loading: false,
          errorMessage: err?.response?.data?.message ?? "Failed to generate URL",
        }));
      },
    }
  );

  async function uploadToS3(file, presignedUrl) {
    try {
      toast.info(`Uploading: ${file.name}`);

      const response = await fetch(presignedUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
      });

      console.log("S3 Upload Response:", response);

      if (!response.ok) {
        throw new Error(
          `Upload failed: ${response.status} ${response.statusText}`
        );
      }

      toast.success("File uploaded successfully");
      return true; // Indicates success
    } catch (error) {
      console.error("Error uploading:", error);
      toast.error(error?.message || "Failed to upload file to S3");
      return false; // Indicates failure
    }
  }

  async function handlePresignedUrlUploadSuccess(success, uploadPayload) {
    console.log("File Details:", uploadPayload);

    const { presignedUrl, objectUrl } = success?.data ?? {};

    if (!presignedUrl || !objectUrl) {
      toast.error("Failed to generate presigned URL");
      return { error: "Presigned URL generation failed" };
    }

    const uploadSuccess = await uploadToS3(uploadPayload, presignedUrl);

    if (!uploadSuccess) {
      return { error: "S3 Upload Failed" };
    }

    return { fileURL: objectUrl };
  }

  async function uploadFile(
    fileName,
    moduleName,
    screenName,
    docId,
    uploadPayload
  ) {
    setState((prev) => ({ ...prev, loading: true }));

    try {
      const mutateFunction = useIndividualUpload
        ? generatePresignedUrlByIndividualMutate
        : generatePresignedUrlBySchoolMutate;

      const payload = {
        fileName,
        module: moduleName,
        screenName,
      };

      // Only include session for school upload
      if (!useIndividualUpload) {
        payload.session = selectedSession || "2026-2027";
      }

      const apiResponse = await mutateFunction(payload);

      const result = await handlePresignedUrlUploadSuccess(
        apiResponse,
        uploadPayload
      );

      setState((prev) => ({
        ...prev,
        loading: false,
        errorMessage: result?.error || "",
      }));

      return result;
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error("Error uploading file");

      setState((prev) => ({
        ...prev,
        loading: false,
        errorMessage: "Error uploading file",
      }));

      return { error: "Upload failed" };
    }
  }

  async function handleUploadImage(
    isFile = false,
    e,
    sessionStatus,
    moduleName,
    screenName,
    docId
  ) {
    setState((prev) => ({ ...prev, loading: true }));

    try {
      const file = isFile ? e : e.target.files[0];
      if (!file) throw new Error("No file selected");

      const folderPrefix =
        sessionStatus === "withSession" ? `${selectedSession}/` : "";
      const s3FilePath = `${schoolDocId}/${folderPrefix}${moduleName}/${screenName}/${formattedName(
        file.name
      )}`;

      const uploadResult = await uploadFile(
        formattedName(file.name),
        moduleName,
        screenName,
        docId,
        file
      );

      if (uploadResult?.error) {
        throw new Error(uploadResult.error);
      }

      setState((prev) => ({
        ...prev,
        profilePic: uploadResult.fileURL || "",
        loading: false,
        errorMessage: "",
      }));

      uploadS3FileMutate({
        module: moduleName,
        screenName: screenName,
        fileName: formattedName(file.name),
        fileUrl: uploadResult.fileURL,
        filePath: s3FilePath,
        fileType: file.type,
        fileSizeInBytes: file.size,
      });

      return uploadResult;
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error(error.message);

      setState((prev) => ({
        ...prev,
        loading: false,
        errorMessage: error.message,
      }));

      return { error: error.message };
    }
  }

  return { state, handleUploadImage, setState };
};

export default useFolderWiseFileUploader;
